/**
 * The real watchlist backend: `/api/clank/alerts/` and the per-alert runs
 * endpoint, read through the same snapshot-and-subscribe pair the mock
 * exposed. The store keeps one assembled list — each alert with its
 * successful runs and the read state derived from them — and reloads it
 * whole after every mutation (except the read acknowledgement, whose effect
 * is known exactly); at watchlist sizes that is cheaper than being clever.
 */
import type { ChatThinkingKind, ChatWsEvent } from "../api";
import { type ClankPublicMessage, getWsUrl, get_company_api_url } from "../api";
import type { components } from "../api/openapi/company-router";
import { createCompanyRouterClient, expectData, expectOk } from "../api/openapi_clients";
import type { ChatEntry } from "../chat/chatStore";
import { IS_STATIC_DEMO_FRONTEND } from "../globals";
import type {
  Alert,
  AlertDraft,
  AlertReview,
  AlertReviewContext,
  AlertRun,
  Schedule,
  WatchlistScope,
} from "./types";
import { hasProcessSelection } from "./types";

type AlertDb = components["schemas"]["AlertDb"];
type WireAlertRun = components["schemas"]["AlertRun"];
type AlertForm = components["schemas"]["AlertForm"];
type WireScope = components["schemas"]["AlertScope"];

let alerts: Alert[] = [];
let fetched = false;

const listeners = new Set<() => void>();

const commit = (next: Alert[]) => {
  alerts = next;
  for (const listener of listeners) listener();
};

/** Stable between mutations, so it can back `useSyncExternalStore`. */
export const alerts_snapshot = (): Alert[] => alerts;

export const subscribe_alerts = (listener: () => void): (() => void) => {
  listeners.add(listener);
  // The sidebar's unread dot subscribes from every route, so the first
  // subscriber anywhere pulls the data.
  if (!fetched) {
    fetched = true;
    void refresh_alerts().catch(() => undefined);
  }
  return () => {
    listeners.delete(listener);
  };
};

/**
 * An alert row plus its runs, as the UI reads them. `null` for payload
 * versions this frontend does not know. Unknown result versions are dropped,
 * but failed runs are kept so the user can see the localized backend reason.
 */
const toAlert = (db: AlertDb, wireRuns: WireAlertRun[]): Alert | null => {
  if (db.payload.version !== "1") return null;
  const { prompt, title, scope, schedule } = db.payload;

  const runs: AlertRun[] = [];
  let unread = false;
  for (const run of wireRuns) {
    if (run.error) {
      runs.push({
        id: run.id,
        ranAt: run.finished_at,
        status: "failed",
        error: run.error,
      });
      unread ||= run.seen_at == null;
      continue;
    }
    if (run.result?.version !== "1") continue;
    // Rows written before the three-part output carry only `summary` (then
    // the full report): reuse it wherever the newer fields are empty.
    const summary = run.result.summary ?? "";
    runs.push({
      id: run.id,
      ranAt: run.finished_at,
      status: "success",
      response: run.result.response || summary,
      headline: run.result.headline ?? "",
      summary,
    });
    unread ||= run.seen_at == null;
  }

  return {
    id: db.id,
    prompt,
    title: title ?? "",
    scope: {
      userIds: scope.user_ids ?? [],
      processSlugs: scope.process_slugs ?? [],
      window: { days: scope.window_days },
    },
    timezone: scope.timezone ?? "UTC",
    periodicity: db.periodicity,
    schedule: schedule ? { everyDays: schedule.every_days } : null,
    enabled: db.enabled,
    unread,
    createdAt: db.created_at,
    editedAt: db.edited_at,
    runs,
  };
};

let inflight: Promise<void> | null = null;

/** Reload the whole list; concurrent calls share one fetch. */
export const refresh_alerts = (): Promise<void> => {
  inflight ??= (async () => {
    const client = createCompanyRouterClient();
    const list = expectData(await client.GET("/api/clank/alerts/", {}), "Failed to load alerts");
    // There is no bulk runs endpoint, so this is one request per alert; the
    // list is small and they run concurrently.
    const runs = await Promise.all(
      list.map(async db =>
        expectData(
          await client.GET("/api/clank/alerts/{alert_id}/runs/", {
            params: { path: { alert_id: db.id } },
          }),
          "Failed to load alert runs",
        ),
      ),
    );
    commit(list.flatMap((db, index) => toAlert(db, runs[index]) ?? []));
  })().finally(() => {
    inflight = null;
  });
  return inflight;
};

// A runner tick can land a burst of runs; trailing-edge debounce so the
// full alerts fan-out (list plus one runs request per alert) is paid once.
const REFRESH_DEBOUNCE_MS = 3_000;
let refreshTimerId: number | undefined;

/** Debounced [`refresh_alerts`], for callers that fire in bursts. */
export const schedule_alerts_refresh = () => {
  window.clearTimeout(refreshTimerId);
  refreshTimerId = window.setTimeout(() => {
    void refresh_alerts().catch(() => undefined);
  }, REFRESH_DEBOUNCE_MS);
};

const wireScope = (scope: WatchlistScope, timezone: string): WireScope => {
  if (!hasProcessSelection(scope)) throw new Error("Select at least one process");
  return {
    user_ids: scope.userIds,
    process_slugs: scope.processSlugs,
    window_days: scope.window.days,
    timezone,
  };
};

/** Returns the new alert's id, with the store already refreshed to hold it. */
export const create_alert = async (draft: AlertDraft): Promise<string> => {
  const client = createCompanyRouterClient();
  const created = expectData(
    await client.POST("/api/clank/alerts/", {
      body: {
        prompt: draft.prompt,
        title: draft.title,
        periodicity: "periodic",
        // The window follows the day borders of wherever the alert was made.
        scope: wireScope(draft.scope, Intl.DateTimeFormat().resolvedOptions().timeZone),
        schedule: { every_days: draft.schedule.everyDays },
        enabled: true,
      },
    }),
    "Failed to create alert",
  );
  await refresh_alerts();
  return created.id;
};

/**
 * Change some of an alert's fields. The endpoint is a full replace, so the
 * rest of the form is filled in from the cached alert.
 */
export const update_alert = async (
  id: string,
  changes: Partial<{
    prompt: string;
    title: string;
    scope: WatchlistScope;
    schedule: Schedule;
    enabled: boolean;
  }>,
): Promise<void> => {
  const alert = alerts.find(item => item.id === id);
  if (!alert) throw new Error("Alert is not in the loaded list");
  const schedule = changes.schedule ?? alert.schedule;
  const body: AlertForm = {
    prompt: changes.prompt ?? alert.prompt,
    title: changes.title ?? alert.title,
    periodicity: alert.periodicity,
    scope: wireScope(changes.scope ?? alert.scope, alert.timezone),
    // A schedule accompanies exactly the periodic alerts; sending one with
    // any other periodicity is rejected.
    schedule:
      alert.periodicity === "periodic" && schedule ? { every_days: schedule.everyDays } : null,
    enabled: changes.enabled ?? alert.enabled,
  };
  const client = createCompanyRouterClient();
  expectData(
    await client.PUT("/api/clank/alerts/{alert_id}/", {
      params: { path: { alert_id: id } },
      body,
    }),
    "Failed to update alert",
  );
  await refresh_alerts();
};

/**
 * Have the model look over an alert before it is saved: it names it, tightens
 * the question, and asks back whatever an unattended evaluation would
 * otherwise have to guess. Give it a draft, a conversation to distil one from,
 * or both.
 *
 * Nothing is saved here, and a failure is never fatal — the callers save
 * unreviewed rather than stranding someone behind a model outage.
 */
export const review_alert = async (draft: {
  prompt?: string;
  /** The scope pills as the author reads them; see the endpoint's own note. */
  scope?: string;
  transcript?: ChatEntry[];
  context?: AlertReviewContext;
}): Promise<AlertReview> => {
  const client = createCompanyRouterClient();
  const review = expectData(
    await client.POST("/api/clank/alerts/review/", {
      body: {
        prompt: draft.prompt ?? "",
        scope: draft.scope ?? "",
        context: draft.context
          ? {
              user_ids: draft.context.scope.userIds,
              process_slugs: draft.context.scope.processSlugs,
              window_days: draft.context.scope.window.days,
              schedule_every_days: draft.context.schedule.everyDays,
              user_options: draft.context.userOptions,
              process_options: draft.context.processOptions,
              touched: draft.context.touched,
            }
          : undefined,
        // Only what was said: the thinking and scope notes in a transcript are
        // how it was rendered, not part of the conversation to distil.
        transcript: (draft.transcript ?? []).flatMap(entry =>
          entry.kind === "message" ? [{ role: entry.role, content: entry.content }] : [],
        ),
      },
    }),
    "Failed to review alert",
  );
  return {
    title: review.title,
    prompt: review.prompt,
    questions: (review.questions ?? []).map(question => ({
      id: question.id,
      question: question.question,
      dimension: question.dimension ?? "definition",
      options: (question.options ?? []).map(option => ({
        label: option.label,
        description: option.description ?? "",
        answer: option.answer ?? option.label,
        recommended: option.recommended ?? false,
      })),
    })),
    scope: {
      userIds: review.scope?.user_ids ?? draft.context?.scope.userIds ?? [],
      processSlugs: review.scope?.process_slugs ?? draft.context?.scope.processSlugs ?? [],
      windowDays: review.scope?.window_days ?? draft.context?.scope.window.days ?? 7,
      scheduleEveryDays:
        review.scope?.schedule_every_days ?? draft.context?.schedule.everyDays ?? 7,
    },
  };
};

export const delete_alert = async (id: string): Promise<void> => {
  const client = createCompanyRouterClient();
  expectOk(
    await client.DELETE("/api/clank/alerts/{alert_id}/", { params: { path: { alert_id: id } } }),
    "Failed to delete alert",
  );
  await refresh_alerts();
};

/** What an in-flight on-request evaluation is doing, or how it ended. */
export type AlertRunProgress =
  | { status: "running"; thinking: ChatThinkingKind | null; text: string }
  | { status: "failed"; message: string; publicMessage?: ClankPublicMessage | null };

let runProgress = new Map<string, AlertRunProgress>();
const runSockets = new Map<string, WebSocket>();

/** Per-alert progress of on-request runs, keyed by alert id. */
export const run_progress_snapshot = (): ReadonlyMap<string, AlertRunProgress> => runProgress;

const setProgress = (id: string, progress: AlertRunProgress | null) => {
  const next = new Map(runProgress);
  if (progress === null) {
    next.delete(id);
  } else {
    next.set(id, progress);
  }
  runProgress = next;
  for (const listener of listeners) listener();
};

/**
 * Ask the backend to evaluate this alert now, mirroring the run's progress
 * into the store. The evaluation is server-owned: a dropped socket does not
 * cancel it, and its run row lands in the runs list either way — this only
 * decides what to show meanwhile. A failed run stays visible until the next
 * attempt replaces it.
 */
export const run_alert = (id: string): void => {
  // The static demo has no evaluation backend: the saved watch just sits in
  // the list, unevaluated, rather than a run socket throwing at the caller.
  if (IS_STATIC_DEMO_FRONTEND) return;
  if (runProgress.get(id)?.status === "running") return;
  setProgress(id, { status: "running", thinking: null, text: "" });

  const ws = new WebSocket(getWsUrl(`/ws/clank/alerts/${id}/run/`, get_company_api_url()));
  runSockets.set(id, ws);
  let settled = false;
  const finish = (progress: AlertRunProgress | null) => {
    if (settled) return;
    settled = true;
    if (runSockets.get(id) === ws) runSockets.delete(id);
    setProgress(id, progress);
    void refresh_alerts().catch(() => undefined);
  };

  ws.onmessage = event => {
    let message: ChatWsEvent;
    try {
      message = JSON.parse(String(event.data)) as ChatWsEvent;
    } catch {
      return;
    }
    const current = runProgress.get(id);
    if (current?.status !== "running") return;
    switch (message.type) {
      case "thinking":
        setProgress(id, { ...current, thinking: message.kind });
        break;
      case "delta":
        setProgress(id, { status: "running", thinking: null, text: current.text + message.text });
        break;
      case "done":
        finish(null);
        break;
      case "error":
        finish({
          status: "failed",
          message: message.message,
          publicMessage: message.public_message ?? null,
        });
        break;
    }
  };
  // A dropped socket is not a failed run — the evaluation continues without
  // us — so just stop showing progress.
  ws.onerror = () => finish(null);
  ws.onclose = () => finish(null);
};

/** Explicitly abort an on-request evaluation; ordinary socket loss still lets it finish. */
export const stop_alert_run = (id: string): void => {
  const ws = runSockets.get(id);
  if (!ws || runProgress.get(id)?.status !== "running") return;
  if (ws.readyState === WebSocket.OPEN) {
    ws.send("stop");
  } else if (ws.readyState === WebSocket.CONNECTING) {
    ws.addEventListener("open", () => ws.send("stop"), { once: true });
  }
  setProgress(id, null);
};

/** Acknowledge every run of one alert, putting out its badge and the sidebar dot. */
export const mark_alert_seen = async (id: string): Promise<void> => {
  const client = createCompanyRouterClient();
  expectOk(
    await client.POST("/api/clank/alerts/{alert_id}/runs/seen/", {
      params: { path: { alert_id: id } },
    }),
    "Failed to mark alert as seen",
  );
  commit(alerts.map(alert => (alert.id === id ? { ...alert, unread: false } : alert)));
};
